import React, { useRef, useState, useEffect } from 'react';
import { Check, X, Users, Pen, Download, RefreshCw, MapPin, Calendar, Clock, Building2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Signature Pad Component with High-Resolution Canvas
const SignaturePad = ({ onSave, onCancel, userName }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // High DPI canvas scaling for better quality
  const SCALE = 2; // 2x resolution for retina displays
  const WIDTH = 400;
  const HEIGHT = 200;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas internal resolution (higher for quality)
    canvas.width = WIDTH * SCALE;
    canvas.height = HEIGHT * SCALE;
    
    // Scale all drawing operations for high DPI
    ctx.scale(SCALE, SCALE);
    
    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    
    // Enhanced stroke settings for smoother lines
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate coordinates accounting for CSS scaling
    const scaleX = WIDTH / rect.width;
    const scaleY = HEIGHT / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <h3 className="text-lg font-semibold mb-2 flex items-center space-x-2">
          <Pen size={20} className="text-[#00D3A8]" />
          <span>Signature de présence</span>
        </h3>
        
        {/* Auto-filled name */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-sm text-gray-600">Copropriétaire :</p>
          <p className="font-semibold text-gray-800">{userName}</p>
          <p className="text-xs text-gray-500 mt-1">
            Horodatage : {new Date().toLocaleString('fr-FR')}
          </p>
        </div>
        
        <div className="border-2 border-gray-300 rounded-lg mb-4 touch-none bg-white">
          <canvas
            ref={canvasRef}
            style={{ width: `${WIDTH}px`, height: `${HEIGHT}px` }}
            className="w-full max-w-full cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        
        <p className="text-sm text-gray-500 mb-4">
          Signez dans le cadre ci-dessus avec votre souris ou votre doigt (coproprietaire en présentiel)
        </p>
        
        <div className="flex justify-between">
          <button
            onClick={clearCanvas}
            className="text-gray-600 hover:text-gray-800 flex items-center space-x-1"
          >
            <RefreshCw size={16} />
            <span>Effacer</span>
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={saveSignature}
              className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]"
            >
              Valider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Attendance Sheet Component
const AttendanceSheet = ({ assembleeId, token, userRole, agInfo, currentUserId }) => {
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignaturePad, setShowSignaturePad] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isLive, setIsLive] = useState(true);
  const [buildingInfo, setBuildingInfo] = useState(null);
  const [showQuickSignModal, setShowQuickSignModal] = useState(false);
  const [proxySignature, setProxySignature] = useState({ show: false, person: null });
  const [bureauSignatures, setBureauSignatures] = useState({});
  const [showBureauSignModal, setShowBureauSignModal] = useState({ show: false, role: null, member: null });

  // Auto-refresh every 5 seconds for live updates
  useEffect(() => {
    fetchAttendance();
    fetchSummary();
    fetchBuildingInfo();

    // Set up auto-refresh interval only if live mode is enabled
    let refreshInterval;
    if (isLive) {
      refreshInterval = setInterval(() => {
        if (!saving && !showSignaturePad) {
          fetchAttendance(true); // silent refresh
          fetchSummary();
          setLastUpdate(new Date());
        }
      }, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [assembleeId, token, isLive, saving, showSignaturePad]);

  const fetchBuildingInfo = async () => {
    if (agInfo?.building_id) {
      try {
        const response = await axios.get(
          `${API}/immeubles/${agInfo.building_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBuildingInfo(response.data);
      } catch (error) {
        console.error('Error fetching building info:', error);
      }
    }
  };

  // Fetch bureau signatures
  useEffect(() => {
    if (agInfo?.bureau) {
      const signatures = {};
      if (agInfo.bureau.president?.signature) {
        signatures.president = agInfo.bureau.president.signature;
      }
      if (agInfo.bureau.secretaire?.signature) {
        signatures.secretaire = agInfo.bureau.secretaire.signature;
      }
      if (agInfo.bureau.scrutateurs) {
        agInfo.bureau.scrutateurs.forEach((s, i) => {
          if (s.signature) {
            signatures[`scrutateur_${i}`] = s.signature;
          }
        });
      }
      setBureauSignatures(signatures);
    }
  }, [agInfo?.bureau]);

  // Save bureau member signature
  const handleBureauSignature = async (signatureData) => {
    const { role, member, index } = showBureauSignModal;
    setSaving(true);
    
    try {
      // Build updated bureau with signature
      const updatedBureau = { ...agInfo.bureau };
      
      if (role === 'president') {
        updatedBureau.president = {
          ...updatedBureau.president,
          signature: signatureData,
          signed_at: new Date().toISOString()
        };
      } else if (role === 'secretaire') {
        updatedBureau.secretaire = {
          ...updatedBureau.secretaire,
          signature: signatureData,
          signed_at: new Date().toISOString()
        };
      } else if (role.startsWith('scrutateur')) {
        const scrutateurs = [...(updatedBureau.scrutateurs || [])];
        scrutateurs[index] = {
          ...scrutateurs[index],
          signature: signatureData,
          signed_at: new Date().toISOString()
        };
        updatedBureau.scrutateurs = scrutateurs;
      }

      // Save to backend
      await axios.post(
        `${API}/assemblees/${assembleeId}/bureau`,
        updatedBureau,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      const key = role.startsWith('scrutateur') ? `scrutateur_${index}` : role;
      setBureauSignatures(prev => ({ ...prev, [key]: signatureData }));
      
      setShowBureauSignModal({ show: false, role: null, member: null });
    } catch (error) {
      console.error('Error saving bureau signature:', error);
      alert('Erreur lors de l\'enregistrement de la signature');
    } finally {
      setSaving(false);
    }
  };

  const fetchAttendance = async (silent = false) => {
    try {
      const response = await axios.get(
        `${API}/assemblees/${assembleeId}/attendance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(
        `${API}/assemblees/${assembleeId}/attendance/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const togglePresence = async (person) => {
    if (userRole !== 'gestionnaire') return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance`,
        { user_id: person.user_id, present: !person.present },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === person.user_id ? { ...p, present: !p.present } : p
      ));
      fetchSummary();
    } catch (error) {
      console.error('Error updating attendance:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSignature = async (signature) => {
    const person = showSignaturePad;
    setShowSignaturePad(null);
    setSaving(true);
    
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance/${person.user_id}/sign`,
        { signature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === person.user_id 
          ? { ...p, signature, present: true, signed_at: new Date().toISOString() } 
          : p
      ));
      fetchSummary();
    } catch (error) {
      console.error('Error saving signature:', error);
    } finally {
      setSaving(false);
    }
  };

  // Quick sign for current user (coproprietaire présent en salle)
  const handleQuickSign = async (signature) => {
    setShowQuickSignModal(false);
    setSaving(true);
    
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance/${currentUserId}/sign`,
        { signature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === currentUserId 
          ? { ...p, signature, present: true, signed_at: new Date().toISOString() } 
          : p
      ));
      fetchSummary();
    } catch (error) {
      console.error('Error saving quick signature:', error);
      alert('Erreur lors de la signature. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  // Proxy signature (gestionnaire signs on behalf of a participant)
  const handleProxySignature = async (signature) => {
    const person = proxySignature.person;
    setProxySignature({ show: false, person: null });
    setSaving(true);
    
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance/${person.user_id}/sign`,
        { 
          signature,
          signed_by_proxy: true,
          proxy_note: `Signé par procuration par le gestionnaire`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === person.user_id 
          ? { ...p, signature, present: true, signed_at: new Date().toISOString(), signed_by_proxy: true } 
          : p
      ));
      fetchSummary();
    } catch (error) {
      console.error('Error saving proxy signature:', error);
      alert('Erreur lors de la signature par procuration.');
    } finally {
      setSaving(false);
    }
  };

  // Get current user's attendance record
  const currentUserAttendance = currentUserId 
    ? attendance.find(p => p.user_id === currentUserId) 
    : null;
  
  // Check if current user can sign (présent en présentiel but hasn't signed)
  const canQuickSign = userRole === 'coproprietaire' 
    && currentUserAttendance 
    && currentUserAttendance.mode === 'presentiel'
    && !currentUserAttendance.signature;

  const updateTantiemes = async (person, tantiemes) => {
    if (userRole !== 'gestionnaire') return;
    
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance`,
        { user_id: person.user_id, tantiemes: parseInt(tantiemes) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === person.user_id ? { ...p, tantiemes: parseInt(tantiemes) } : p
      ));
      fetchSummary();
    } catch (error) {
      console.error('Error updating tantiemes:', error);
    }
  };

  const updateMode = async (person, mode) => {
    if (userRole !== 'gestionnaire') return;
    
    try {
      await axios.post(
        `${API}/assemblees/${assembleeId}/attendance`,
        { user_id: person.user_id, mode: mode || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAttendance(prev => prev.map(p => 
        p.user_id === person.user_id ? { ...p, mode: mode || null } : p
      ));
    } catch (error) {
      console.error('Error updating mode:', error);
    }
  };

  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const exportPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await axios.get(
        `${API}/assemblees/${assembleeId}/attendance/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Format date for filename
      let dateStr = '';
      if (agInfo?.date) {
        try {
          const date = new Date(agInfo.date);
          dateStr = `_${date.toISOString().split('T')[0]}`;
        } catch (e) {
          dateStr = '';
        }
      }
      
      // Create download link with date in filename
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Feuille_Presence_AG${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AG Header with all info */}
      {agInfo && (
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#00A88A] rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <h1 className="text-2xl font-bold">Feuille de Présence</h1>
              <h2 className="text-xl font-semibold opacity-95">{agInfo.titre || 'Assemblée Générale'}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {/* Building */}
                <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
                  <Building2 size={18} className="opacity-80" />
                  <div>
                    <div className="text-xs opacity-75">Immeuble</div>
                    <div className="font-medium text-sm">{buildingInfo?.nom || agInfo.building_name || '—'}</div>
                  </div>
                </div>
                
                {/* Date */}
                <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
                  <Calendar size={18} className="opacity-80" />
                  <div>
                    <div className="text-xs opacity-75">Date</div>
                    <div className="font-medium text-sm">
                      {agInfo.date ? new Date(agInfo.date).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) : '—'}
                    </div>
                  </div>
                </div>
                
                {/* Time */}
                <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
                  <Clock size={18} className="opacity-80" />
                  <div>
                    <div className="text-xs opacity-75">Heure</div>
                    <div className="font-medium text-sm">
                      {agInfo.date_heure ? new Date(agInfo.date_heure).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : agInfo.heure || '—'}
                    </div>
                  </div>
                </div>
                
                {/* Location */}
                <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
                  <MapPin size={18} className="opacity-80" />
                  <div>
                    <div className="text-xs opacity-75">Lieu</div>
                    <div className="font-medium text-sm truncate" title={agInfo.lieu}>
                      {agInfo.lieu || '—'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Additional details */}
              {(agInfo.complement || buildingInfo?.adresse) && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  {buildingInfo?.adresse && (
                    <p className="text-sm opacity-90">
                      <span className="font-medium">Adresse :</span> {buildingInfo.adresse}
                      {buildingInfo.code_postal && ` - ${buildingInfo.code_postal}`}
                      {buildingInfo.ville && ` ${buildingInfo.ville}`}
                    </p>
                  )}
                  {agInfo.complement && (
                    <p className="text-sm opacity-90 mt-1">
                      <span className="font-medium">Complément :</span> {agInfo.complement}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Status badge */}
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              agInfo.status === 'En cours' || agInfo.status === 'Ouverte' 
                ? 'bg-green-500 text-white' 
                : agInfo.status === 'Clôturée' 
                  ? 'bg-gray-500 text-white'
                  : 'bg-yellow-400 text-yellow-900'
            }`}>
              {agInfo.status || 'En cours'}
            </div>
          </div>
        </div>
      )}

      {/* Quick Sign Button for présent en salle coproprietaires */}
      {canQuickSign && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-amber-400 rounded-full flex items-center justify-center">
                <Pen className="text-white" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-900">Signez votre présence</h3>
                <p className="text-amber-700">
                  Vous êtes présent(e) en salle. Signez pour confirmer votre participation à l'AG.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowQuickSignModal(true)}
              disabled={saving}
              data-testid="quick-sign-btn"
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold flex items-center space-x-2 shadow-lg transition-all transform hover:scale-105 disabled:opacity-50"
            >
              <Pen size={20} />
              <span>Signer ma présence</span>
            </button>
          </div>
        </div>
      )}

      {/* Quick Sign Modal */}
      {showQuickSignModal && (
        <SignaturePad
          onSave={handleQuickSign}
          onCancel={() => setShowQuickSignModal(false)}
          userName={currentUserAttendance?.nom ? `${currentUserAttendance.prenom || ''} ${currentUserAttendance.nom}` : 'Votre signature'}
        />
      )}

      {/* Proxy Signature Modal (for gestionnaire) */}
      {proxySignature.show && proxySignature.person && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b bg-amber-50">
              <h3 className="font-bold text-lg text-amber-900">Signature par procuration</h3>
              <p className="text-sm text-amber-700 mt-1">
                Vous signez au nom de <strong>{proxySignature.person.prenom} {proxySignature.person.nom}</strong>
              </p>
              <p className="text-xs text-amber-600 mt-1 italic">
                Une mention "Signé par procuration" sera ajoutée
              </p>
            </div>
            <div className="p-4">
              <SignaturePad
                onSave={handleProxySignature}
                onCancel={() => setProxySignature({ show: false, person: null })}
                userName={`Signature pour ${proxySignature.person.prenom} ${proxySignature.person.nom}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="text-sm text-gray-500">Présents</div>
            <div className="text-2xl font-bold text-green-600">{summary.present}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
            <div className="text-sm text-gray-500">Représentés</div>
            <div className="text-2xl font-bold text-yellow-600">{summary.represented}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="text-sm text-gray-500">Absents</div>
            <div className="text-2xl font-bold text-red-600">{summary.absent}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#00D3A8]">
            <div className="text-sm text-gray-500">Quorum</div>
            <div className="text-2xl font-bold text-[#00D3A8]">{summary.quorum_percentage}%</div>
            <div className="text-xs text-gray-400">
              {summary.present_tantiemes}/{summary.total_tantiemes} tantièmes
            </div>
          </div>
        </div>
      )}

      {/* Bureau Signatures Section */}
      {agInfo?.bureau && (agInfo.bureau.president || agInfo.bureau.secretaire || agInfo.bureau.scrutateurs?.length > 0) && (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Pen size={20} className="text-[#00D3A8]" />
            Signatures du Bureau
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* President */}
            {agInfo.bureau.president && (
              <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-600 uppercase">Président(e) de séance</span>
                  {agInfo.bureau.president.mode === 'visio' && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Visio</span>
                  )}
                </div>
                <p className="font-medium text-gray-900">
                  {agInfo.bureau.president.prenom} {agInfo.bureau.president.nom}
                </p>
                
                {bureauSignatures.president ? (
                  <div className="mt-3">
                    <img 
                      src={bureauSignatures.president} 
                      alt="Signature" 
                      className="h-16 border rounded bg-white p-1"
                    />
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check size={12} /> Signé
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowBureauSignModal({ 
                      show: true, 
                      role: 'president', 
                      member: agInfo.bureau.president 
                    })}
                    disabled={saving}
                    className="mt-3 w-full px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 flex items-center justify-center gap-2"
                  >
                    <Pen size={14} /> Signer
                  </button>
                )}
              </div>
            )}

            {/* Secrétaire */}
            {agInfo.bureau.secretaire && (
              <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-green-600 uppercase">Secrétaire</span>
                  {agInfo.bureau.secretaire.mode === 'visio' && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Visio</span>
                  )}
                </div>
                <p className="font-medium text-gray-900">
                  {agInfo.bureau.secretaire.prenom} {agInfo.bureau.secretaire.nom}
                </p>
                
                {bureauSignatures.secretaire ? (
                  <div className="mt-3">
                    <img 
                      src={bureauSignatures.secretaire} 
                      alt="Signature" 
                      className="h-16 border rounded bg-white p-1"
                    />
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check size={12} /> Signé
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowBureauSignModal({ 
                      show: true, 
                      role: 'secretaire', 
                      member: agInfo.bureau.secretaire 
                    })}
                    disabled={saving}
                    className="mt-3 w-full px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center justify-center gap-2"
                  >
                    <Pen size={14} /> Signer
                  </button>
                )}
              </div>
            )}

            {/* Scrutateurs */}
            {agInfo.bureau.scrutateurs?.map((scrutateur, index) => (
              <div key={index} className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-amber-600 uppercase">Scrutateur {index + 1}</span>
                  {scrutateur.mode === 'visio' && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Visio</span>
                  )}
                </div>
                <p className="font-medium text-gray-900">
                  {scrutateur.prenom} {scrutateur.nom}
                </p>
                
                {bureauSignatures[`scrutateur_${index}`] ? (
                  <div className="mt-3">
                    <img 
                      src={bureauSignatures[`scrutateur_${index}`]} 
                      alt="Signature" 
                      className="h-16 border rounded bg-white p-1"
                    />
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check size={12} /> Signé
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowBureauSignModal({ 
                      show: true, 
                      role: `scrutateur_${index}`, 
                      member: scrutateur,
                      index 
                    })}
                    disabled={saving}
                    className="mt-3 w-full px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 flex items-center justify-center gap-2"
                  >
                    <Pen size={14} /> Signer
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bureau Signature Modal */}
      {showBureauSignModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b bg-[#00D3A8]/10">
              <h3 className="font-bold text-lg text-gray-900">Signature - {showBureauSignModal.role === 'president' ? 'Président(e)' : showBureauSignModal.role === 'secretaire' ? 'Secrétaire' : 'Scrutateur'}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {showBureauSignModal.member?.prenom} {showBureauSignModal.member?.nom}
                {showBureauSignModal.member?.mode === 'visio' && (
                  <span className="ml-2 text-purple-600">(participation à distance)</span>
                )}
              </p>
            </div>
            <div className="p-4">
              <SignaturePad
                onSave={handleBureauSignature}
                onCancel={() => setShowBureauSignModal({ show: false, role: null, member: null })}
                userName={`${showBureauSignModal.member?.prenom || ''} ${showBureauSignModal.member?.nom || ''}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold flex items-center space-x-2">
            <Users size={20} />
            <span>Liste des coproprietaires</span>
          </h2>
          
          {/* Live indicator */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsLive(!isLive)}
              className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                isLive 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              data-testid="live-toggle-btn"
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`}></span>
              <span>{isLive ? 'LIVE' : 'PAUSE'}</span>
            </button>
            <span className="text-xs text-gray-400">
              Màj: {lastUpdate.toLocaleTimeString('fr-FR')}
            </span>
            <button
              onClick={() => { fetchAttendance(); fetchSummary(); setLastUpdate(new Date()); }}
              className="p-1 text-gray-400 hover:text-[#00D3A8] rounded transition-colors"
              title="Rafraîchir maintenant"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
        
        <button
          onClick={exportPDF}
          disabled={downloadingPDF}
          className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
          data-testid="export-attendance-pdf-btn"
        >
          {downloadingPDF ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <Download size={18} />
          )}
          <span>Exporter PDF</span>
        </button>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Copropriétaire</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tantièmes</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mode</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Connexion</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Présent</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Signature</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {attendance.map((person) => (
              <tr key={person.user_id} className={person.present ? 'bg-green-50' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">
                    {person.prenom} {person.nom}
                  </div>
                  <div className="text-sm text-gray-500">{person.email}</div>
                </td>
                <td className="px-4 py-3">
                  {userRole === 'gestionnaire' ? (
                    <input
                      type="number"
                      value={person.tantiemes || 0}
                      onChange={(e) => updateTantiemes(person, e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                    />
                  ) : (
                    <span>{person.tantiemes || 0}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {userRole === 'gestionnaire' ? (
                    <select
                      value={person.mode || ''}
                      onChange={(e) => updateMode(person, e.target.value)}
                      className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                        person.mode === 'video' ? 'bg-green-100 text-green-700' :
                        person.mode === 'audio' ? 'bg-blue-100 text-blue-700' :
                        person.mode === 'correspondance' ? 'bg-amber-100 text-amber-700' :
                        person.mode === 'presentiel' ? 'bg-purple-100 text-purple-700' :
                        person.mode === 'delegation' ? 'bg-gray-100 text-gray-700' :
                        'bg-gray-100 text-gray-500'
                      }`}
                      data-testid={`mode-select-${person.user_id}`}
                    >
                      <option value="">— Non défini</option>
                      <option value="video">📹 Vidéo</option>
                      <option value="audio">🎤 Audio</option>
                      <option value="presentiel">🏢 Présentiel</option>
                      <option value="correspondance">📧 Correspondance</option>
                      <option value="delegation">👥 Délégation</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      person.mode === 'video' ? 'bg-green-100 text-green-700' :
                      person.mode === 'audio' ? 'bg-blue-100 text-blue-700' :
                      person.mode === 'correspondance' ? 'bg-amber-100 text-amber-700' :
                      person.mode === 'presentiel' ? 'bg-purple-100 text-purple-700' :
                      person.mode === 'delegation' ? 'bg-gray-100 text-gray-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {person.mode === 'video' ? '📹 Vidéo' :
                       person.mode === 'audio' ? '🎤 Audio' :
                       person.mode === 'correspondance' ? '📧 Corresp.' :
                       person.mode === 'presentiel' ? '🏢 Présentiel' :
                       person.mode === 'delegation' ? '👥 Délégation' :
                       '—'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {person.connected_at ? (
                    <span className="text-sm text-gray-600">
                      {new Date(person.connected_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {person.mode === 'presentiel' ? (
                    /* Pour les présentiels: la présence est validée par la signature */
                    <div className="flex flex-col items-center">
                      {person.signature ? (
                        <span className="w-8 h-8 rounded-full flex items-center justify-center bg-green-500 text-white">
                          <Check size={18} />
                        </span>
                      ) : (
                        <span className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-100 text-amber-600 border-2 border-amber-300">
                          <Pen size={14} />
                        </span>
                      )}
                      <span className="text-xs text-gray-500 mt-1">
                        {person.signature ? 'Signé' : 'À signer'}
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => togglePresence(person)}
                      disabled={userRole !== 'gestionnaire' || saving}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        person.present 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                      } ${userRole !== 'gestionnaire' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {person.present ? <Check size={18} /> : <X size={18} />}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {person.signature ? (
                    <div className="inline-block">
                      <img 
                        src={person.signature} 
                        alt="Signature" 
                        className="h-12 border border-gray-200 rounded"
                      />
                      <div className="text-xs text-green-600 mt-1 font-medium">
                        ✓ {person.signed_at ? new Date(person.signed_at).toLocaleTimeString('fr-FR') : ''}
                        {person.signed_by_proxy && (
                          <span className="block text-amber-600">(par procuration)</span>
                        )}
                      </div>
                    </div>
                  ) : person.mode === 'presentiel' ? (
                    userRole === 'gestionnaire' ? (
                      <div className="flex flex-col items-center space-y-2">
                        <button
                          onClick={() => setShowSignaturePad(person)}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors animate-pulse"
                          data-testid={`sign-btn-${person.user_id}`}
                        >
                          <Pen size={16} />
                          <span className="font-medium">Signer</span>
                        </button>
                        <button
                          onClick={() => setProxySignature({ show: true, person })}
                          className="text-xs text-amber-600 hover:text-amber-700 underline"
                          data-testid={`proxy-sign-btn-${person.user_id}`}
                        >
                          Signer par procuration
                        </button>
                      </div>
                    ) : person.user_id === currentUserId ? (
                      <button
                        onClick={() => setShowQuickSignModal(true)}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                        data-testid="sign-myself-btn"
                      >
                        <Pen size={16} />
                        <span className="font-medium">Signer</span>
                      </button>
                    ) : (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">En attente</span>
                    )
                  ) : !person.mode ? (
                    userRole === 'gestionnaire' ? (
                      <button
                        onClick={() => setShowSignaturePad(person)}
                        className="text-gray-400 hover:text-[#00D3A8] flex items-center space-x-1 mx-auto"
                      >
                        <Pen size={16} />
                        <span>Signer</span>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">—</span>
                    )
                  ) : (
                    <span className="text-xs text-gray-400">Électronique</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {attendance.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun coproprietaire enregistré
          </div>
        )}
      </div>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignature}
          onCancel={() => setShowSignaturePad(null)}
          userName={`${showSignaturePad.prenom} ${showSignaturePad.nom}`}
        />
      )}
    </div>
  );
};

export default AttendanceSheet;
